import React, { useMemo } from 'react';

import { degreesToSlope, Direction2D, Point2D } from '@paragon/geometry-library';
import { Point3D } from '@paragon/three-shared';

import { SLOPE_CAP } from '../hooks/useMousePositionWithBasicInsertionSnapping';
import { LabelVisual } from '../util/LabelVisual';
import { Line } from '../util/Line';
import { useDimensionStyle } from './dimensionStyles';

const LABEL_OFFSET = 6;
const LINE_WIDTH = 0.15;
const MIN_LINE_DISTANCE = 8;
const MAX_LINE_DISTANCE = 65;

const slopeLabelFontSize = 5;
const yAdjustmentForMultilineSlopeLabel = slopeLabelFontSize / 2;

interface Props {
  readonly basePoint: Point3D;
  readonly baseLineEndPoint: Point3D;
  readonly endLineEndPoint: Point3D;
  readonly showDegreesInLabel?: boolean;
  readonly showPitchInLabel?: boolean;
}

/**
 * An alternative to AngleDimension that 1) uses react-three-fiber, 2) is visually cleaner, and 3) supports slope.
 */
export function SimpleSlopeDimension(props: Props) {
  const basePoint = Point2D.fromObject(props.basePoint);
  const baseLineEndPoint = Point2D.fromObject(props.baseLineEndPoint);
  const endLineEndPoint = Point2D.fromObject(props.endLineEndPoint);

  const angleInDegrees = Direction2D.fromPoints(
    Point2D.fromObject(basePoint),
    Point2D.fromObject(endLineEndPoint),
  ).angleTo(Direction2D.fromPoints(Point2D.fromObject(basePoint), Point2D.fromObject(baseLineEndPoint)));

  const absoluteAngleInDegrees = Math.abs(angleInDegrees);

  const radius = Math.max(MIN_LINE_DISTANCE, Math.min(MAX_LINE_DISTANCE, basePoint.distanceTo(endLineEndPoint)));

  const angleFromHorizontal = Math.atan2(endLineEndPoint.y - basePoint.y, Math.abs(endLineEndPoint.x - basePoint.x));
  const thetaLength = Math.abs(angleFromHorizontal);

  let thetaStart;
  if (endLineEndPoint.y >= basePoint.y) {
    if (endLineEndPoint.x >= basePoint.x) {
      thetaStart = 0; // quadrant 1
    } else {
      thetaStart = Math.PI - thetaLength; // quadrant 2
    }
  } else if (endLineEndPoint.x >= basePoint.x) {
    thetaStart = Math.PI * 2 - thetaLength; // quadrant 4
  } else {
    thetaStart = Math.PI; // quadrant 3
  }

  let labelAngleFromHorizontal = angleFromHorizontal / 2;

  if (endLineEndPoint.x < basePoint.x) {
    labelAngleFromHorizontal = Math.PI - labelAngleFromHorizontal;
  }

  const labelPosition = useMemo(
    () => ({
      x: basePoint.x + (radius + LABEL_OFFSET) * Math.cos(labelAngleFromHorizontal),
      y: basePoint.y + (radius + LABEL_OFFSET) * Math.sin(labelAngleFromHorizontal),
      z: 0,
    }),
    [basePoint.x, basePoint.y, radius, labelAngleFromHorizontal],
  );

  const baseLinePoints = useMemo(
    () =>
      [
        [basePoint.x, basePoint.y, 0],
        [basePoint.x + (endLineEndPoint.x >= basePoint.x ? radius : -radius), basePoint.y, 0],
      ] as const,
    [basePoint.x, basePoint.y, endLineEndPoint.x, radius],
  );

  const { material, labelOptions } = useDimensionStyle('standard');

  const slope = Math.round(degreesToSlope(absoluteAngleInDegrees) * 100) / 100;
  const degreesText = `${Math.round(absoluteAngleInDegrees * 100) / 100}° `;
  const slopeText = props.showPitchInLabel === false || slope > SLOPE_CAP ? null : `${slope} / 12`;

  const degreesLabelYAdjustment = slopeText ? yAdjustmentForMultilineSlopeLabel : 0;
  const slopeLabelYAdjustment = -yAdjustmentForMultilineSlopeLabel;

  return (
    <>
      <mesh position={[props.basePoint.x, props.basePoint.y, props.basePoint.z]} material={material}>
        <ringGeometry
          args={[
            radius - LINE_WIDTH / 2, // innerRadius
            radius + LINE_WIDTH / 2, // outerRadius
            32, // thetaSegments
            1, // phiSegments
            thetaStart,
            thetaLength,
          ]}
        />
      </mesh>

      <Line points={baseLinePoints} color={labelOptions.color} depthTest={false} />

      <LabelVisual
        {...labelOptions}
        fontSize={slopeLabelFontSize}
        position={[labelPosition.x, labelPosition.y + degreesLabelYAdjustment, labelPosition.z]}
      >
        {degreesText}
      </LabelVisual>
      {slopeText && (
        <LabelVisual
          {...labelOptions}
          fontSize={slopeLabelFontSize}
          position={[labelPosition.x, labelPosition.y + slopeLabelYAdjustment, labelPosition.z]}
        >
          {slopeText}
        </LabelVisual>
      )}
    </>
  );
}
